/**
 * Resolves which reference duration the "+X min" pill on route cards
 * should compare against.
 *
 * Three explicit baselines are supported:
 *   - "google"   — Google Maps' driving ETA for the same A→B
 *   - "fastest"  — our fastest classified route
 *   - "pleasant" — our pleasant classified route
 *
 * Plus an "auto" mode that prefers Google → fastest → pleasant.
 *
 * Explicit picks fall back through the same order when the chosen
 * reference is unavailable, so the toggle never strands users on an
 * empty baseline.
 */
export type BaselineMode = "auto" | "google" | "fastest" | "pleasant";

export type ResolvedBaselineMode = "google" | "fastest" | "pleasant";

export interface BaselineOptions {
  google: number | undefined;
  fastest: number | undefined;
  pleasant: number | undefined;
}

export interface ResolvedBaseline {
  mode: ResolvedBaselineMode;
  /** Reference duration in seconds, or undefined when no baseline is available. */
  duration: number | undefined;
}

export function resolveBaseline(
  mode: BaselineMode,
  options: BaselineOptions,
): ResolvedBaseline {
  const order: ResolvedBaselineMode[] =
    mode === "auto"
      ? ["google", "fastest", "pleasant"]
      : [mode, "google", "fastest", "pleasant"];
  for (const m of order) {
    const d = options[m];
    if (d != null) return { mode: m, duration: d };
  }
  // No baseline available at all.
  return { mode: "fastest", duration: undefined };
}
